import { z } from 'zod';
import { currencyScale, majorToMinor, minorToMajor } from '@wit-support/ui-domain';
import { adsTypeOf } from './structured-display.js';
/** Имя поля в форме: точки ключа → `__`. */
export function fieldName(key) {
    return key.replace(/\./g, '__');
}
/** Ключ реестра из имени поля формы. */
export function fieldKeyOf(name) {
    return name.replace(/__/g, '.');
}
/** Типы, у которых нет поля ввода в формах реестра (рисуются отдельным блоком экрана). */
export const NON_FORM_TYPES = new Set(['creative_items']);
/** У поля есть варианты выбора (enum) — значения опций строками для виджетов. */
function optionStrings(desc) {
    return (desc.options ?? []).map((o) => String(o.value));
}
function isBlank(v) {
    if (v === undefined || v === null)
        return true;
    if (typeof v === 'string')
        return v.trim() === '';
    if (Array.isArray(v))
        return v.length === 0;
    return false;
}
// --- контракт ↔ форма -------------------------------------------------------
export function toUiValue(desc, value, currency) {
    if (value === undefined)
        return undefined;
    switch (desc.type) {
        case 'money':
            return typeof value === 'number' ? minorToMajor(value, currency) : undefined;
        case 'enum':
            return value === null ? undefined : String(value);
        case 'int_list':
            return Array.isArray(value) ? value.map((v) => String(v)) : [];
        case 'country_codes':
            return Array.isArray(value) ? value.map((v) => String(v)) : [];
        case 'text':
        case 'textarea':
        case 'url':
            return value === null ? '' : value;
        default:
            return value;
    }
}
export function fromUiValue(desc, ui, currency) {
    switch (desc.type) {
        case 'money':
            return typeof ui === 'number' && Number.isFinite(ui) ? majorToMinor(ui, currency) : ui;
        case 'enum': {
            if (ui === undefined || ui === null || ui === '')
                return undefined;
            const opt = (desc.options ?? []).find((o) => String(o.value) === String(ui));
            return opt ? opt.value : ui;
        }
        case 'int_list':
            return Array.isArray(ui) ? ui.map((s) => Number(String(s).replace(',', '.'))) : ui;
        case 'country_codes':
            return Array.isArray(ui) ? ui.map((s) => String(s).trim().toUpperCase()) : ui;
        case 'text':
        case 'textarea':
        case 'url':
            return typeof ui === 'string' && ui.trim() === '' ? undefined : ui;
        default:
            return ui;
    }
}
/** Контрактные значения полей → значения формы (по `fieldName`). */
export function toFormValues(descs, values, opts = {}) {
    const out = {};
    for (const d of descs) {
        if (NON_FORM_TYPES.has(d.type))
            continue;
        out[fieldName(d.key)] = toUiValue(d, values[d.key], opts.currency);
    }
    return out;
}
/**
 * Значения формы → контрактные (по ключу реестра). Пустые (`undefined`)
 * отбрасываются: «не задано» значит «как в шаблоне / по умолчанию».
 */
export function fromFormValues(descs, form, opts = {}) {
    const out = {};
    if (!form)
        return out;
    for (const d of descs) {
        if (NON_FORM_TYPES.has(d.type))
            continue;
        const name = fieldName(d.key);
        if (!(name in form))
            continue;
        const v = fromUiValue(d, form[name], opts.currency);
        if (v !== undefined)
            out[d.key] = v;
    }
    if (opts.context === undefined)
        return out;
    const merged = { ...opts.context, ...out };
    for (const d of descs) {
        if (d.key in out && !isVisible(d, merged))
            delete out[d.key];
    }
    return out;
}
/** Значение по умолчанию поля (контрактное): `default` дескриптора. */
export function defaultValueOf(desc) {
    return desc.default === null ? undefined : desc.default;
}
// --- видимость ---------------------------------------------------------------
/**
 * `visibleWhen` — поле видно, только если значение поля `key` входит в `in`.
 * Значения сравниваются строками (опции бывают числами/строками).
 */
export function isVisible(desc, values) {
    const vw = desc.visibleWhen;
    if (vw == null)
        return true;
    const cur = values[vw.key];
    return vw.in.some((x) => String(x) === String(cur));
}
// --- проверка значения -------------------------------------------------------
function fmtNum(desc, n, currency) {
    if (desc.type !== 'money')
        return String(n);
    return minorToMajor(n, currency).toFixed(currencyScale(currency)).replace('.', ',');
}
/**
 * Проверка контрактного значения по `constraints` дескриптора. null — ок.
 * Сообщения — по-русски, как их увидит оператор под полем.
 */
export function validateValue(desc, value, currency) {
    const c = desc.constraints;
    if (isBlank(value))
        return c?.required ? 'Обязательное поле' : null;
    switch (adsTypeOf(desc)) {
        case 'daily_quota': {
            // лимит проверяется и при «Не проверять»: auto-ads требует его всегда
            const q = value;
            const lo = c?.min ?? 1;
            const hi = c?.max ?? 500;
            if (typeof q?.limit !== 'number' ||
                !Number.isInteger(q.limit) ||
                q.limit < lo ||
                q.limit > hi) {
                return `Лимит запусков в день — целое число от ${lo} до ${hi}`;
            }
            return null;
        }
        case 'money':
        case 'int':
        case 'decimal': {
            if (typeof value !== 'number' || !Number.isFinite(value))
                return 'Нужно число';
            if (desc.type === 'int' && !Number.isInteger(value))
                return 'Нужно целое число';
            if (c?.min != null && value < c.min)
                return `Не меньше ${fmtNum(desc, c.min, currency)}`;
            if (c?.max != null && value > c.max)
                return `Не больше ${fmtNum(desc, c.max, currency)}`;
            return null;
        }
        case 'int_range': {
            const r = value;
            if (typeof r?.min !== 'number' || typeof r?.max !== 'number')
                return 'Укажите «от» и «до»';
            if (r.min > r.max)
                return '«От» больше «до»';
            if (c?.min != null && r.min < c.min)
                return `Не меньше ${c.min}`;
            if (c?.max != null && r.max > c.max)
                return `Не больше ${c.max}`;
            return null;
        }
        case 'int_list': {
            if (!Array.isArray(value))
                return 'Нужен список чисел';
            if (value.some((v) => typeof v !== 'number' || !Number.isFinite(v))) {
                return 'Только числа';
            }
            if (c?.maxItems != null && value.length > c.maxItems)
                return `Не больше ${c.maxItems}`;
            return null;
        }
        case 'text':
        case 'textarea':
        case 'url': {
            if (typeof value !== 'string')
                return 'Нужен текст';
            if (desc.type === 'url') {
                try {
                    const u = new URL(value);
                    if (u.protocol !== 'https:')
                        return 'Ссылка — только https://';
                }
                catch {
                    return 'Неверная ссылка';
                }
            }
            if (c?.pattern) {
                try {
                    if (!new RegExp(c.pattern).test(value))
                        return 'Неверный формат';
                }
                catch {
                    // кривой pattern в реестре — пусть проверит сервер
                }
            }
            const missing = (c?.mustContain ?? []).filter((s) => !value.includes(s));
            if (missing.length > 0)
                return mustContainMessage(missing);
            return null;
        }
        case 'geo': {
            const g = value;
            if (g?.mode === 'COUNTRIES' && (!Array.isArray(g.countries) || g.countries.length === 0)) {
                return 'Укажите хотя бы одну страну';
            }
            return null;
        }
        case 'start': {
            const s = value;
            if (s?.mode === 'at' && (typeof s.at !== 'string' || s.at === '')) {
                return 'Укажите дату и время';
            }
            return null;
        }
        default: {
            if (Array.isArray(value) && c?.maxItems != null && value.length > c.maxItems) {
                return `Не больше ${c.maxItems}`;
            }
            return null;
        }
    }
}
/**
 * Текст ошибки «не хватает обязательной части значения» — по-русски, без
 * служебных макросов Meta (`{{adset.id}}`, спека v4 §11.9): метка группы
 * объявлений в параметрах ссылки — «не удаляйте…», прочее — «Должно содержать».
 */
export const URL_TAGS_ADSET_REQUIRED_MESSAGE = 'Не удаляйте метку группы объявлений из параметров ссылки — по ней работает аналитика';
export function mustContainMessage(missing) {
    if (missing.some((m) => m.startsWith('utm_medium=') || m.includes('{{'))) {
        return URL_TAGS_ADSET_REQUIRED_MESSAGE;
    }
    return `Должно содержать: ${missing.join(', ')}`;
}
/**
 * Схема группы полей реестра для `ZodForm`: по полю `fieldName(key)` на
 * дескриптор (`z.unknown()` — виджет подбирается по схеме, переданной в
 * `ZodField` явно, см. `widgetSchemaFor`), проверка — в `superRefine` и
 * только у ВИДИМЫХ полей: спрятанное `visibleWhen` поле со старым значением
 * не блокирует сохранение.
 */
export function buildRegistrySchema(descs, opts = {}) {
    const formDescs = descs.filter((d) => !NON_FORM_TYPES.has(d.type));
    const shape = {};
    for (const d of formDescs)
        shape[fieldName(d.key)] = z.unknown();
    return z.object(shape).superRefine((vals, ctx) => {
        const currency = opts.currency?.() ?? null;
        const own = fromFormValues(formDescs, vals, { currency });
        const all = { ...(opts.context?.() ?? {}), ...own };
        for (const d of formDescs) {
            if (!isVisible(d, all))
                continue;
            if (opts.isActive && !opts.isActive(d, all))
                continue;
            const msg = validateValue(d, own[d.key], currency);
            if (msg)
                ctx.addIssue({ code: 'custom', message: msg, path: [fieldName(d.key)] });
        }
    });
}
/**
 * Схема ОДНОГО поля для подбора виджета `ZodField` (`schema` prop) — в
 * форменном представлении, с `meta` (подпись, подсказка, опции). Для типов с
 * собственным виджетом (`field-widget.tsx`) схема — носитель meta, не более.
 */
export function widgetSchemaFor(desc, currency) {
    const meta = {
        label: desc.label,
        adsType: desc.type,
        adsMode: desc.mode,
    };
    if (desc.hint)
        meta.description = desc.hint;
    // обязательное поле помечено звёздочкой: значений по умолчанию нет — пустое видно сразу
    if (desc.constraints?.required === true)
        meta.required = true;
    switch (desc.type) {
        case 'enum': {
            const values = optionStrings(desc);
            if (values.length === 0)
                return z.string().meta(meta);
            meta.options = (desc.options ?? []).map((o) => ({ value: String(o.value), label: o.label }));
            meta.placeholder = 'Выберите…';
            return z.enum(values).meta(meta);
        }
        case 'money': {
            const digits = currencyScale(currency);
            return z.number().meta({
                ...meta,
                ...(currency ? { label: `${desc.label}, ${currency.toUpperCase()}` } : {}),
                widget: 'decimal',
                placeholder: digits > 0 ? `0,${'0'.repeat(digits)}` : '0',
            });
        }
        case 'decimal':
            return z.number().meta({ ...meta, widget: 'decimal' });
        case 'int':
            return z.number().int().meta(meta);
        case 'bool':
            return z.boolean().meta(meta);
        case 'textarea':
            return z.string().meta({ ...meta, widget: 'textarea' });
        case 'url':
            return z
                .string()
                .url()
                .meta({ ...meta, placeholder: 'https://' });
        case 'text':
            return z.string().meta(meta);
        case 'int_list':
            return z.array(z.string()).meta({ ...meta, placeholder: 'Число и Enter' });
        case 'country_codes':
            return z.array(z.string()).meta({ ...meta, placeholder: 'Страна или код…' });
        default:
            if (desc.options != null) {
                meta.adsOptions = desc.options.map((o) => ({ value: String(o.value), label: o.label }));
            }
            return z.unknown().meta({ ...meta, widget: `ads:${desc.type}` });
    }
}
//# sourceMappingURL=to-zod.js.map